/**
 * NO03 - Fakturakopi List Component
 * Displays search results in a table with infinite scrolling
 */

import { useRef, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import NO03TableHead from './NO03TableHead';
import NO03 from '../../../models/NO03';
import * as APICalls from '../../../felles/APICalls_NO03';
import * as actionsNO03 from '../../../store/actions/indexNO03';

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: 0,
    borderRadius: 0,
    boxShadow: 'none',
    borderTop: '1px solid #e0e0e0',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  headerSection: {
    padding: theme.spacing(1, 2),
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
  },
  resultSummary: {
    fontWeight: 500,
    fontSize: '1.1rem',
  },
  emptyState: {
    padding: theme.spacing(8),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    padding: theme.spacing(8),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cell: {
    fontSize: '0.82rem',
    padding: '6px 10px',
  },
  amountNegative: {
    color: theme.palette.error.main,
  },
  amountPositive: {
    color: theme.palette.success.main,
  },
  loadMoreContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  creditNote: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.18) !important',
    },
  },
}));

export default function NO03List() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [sortBy, setSortBy] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const invoices = useSelector((state: AppState) => state.NO03.invoices);
  const totalAmount = useSelector((state: AppState) => state.NO03.totalAmount);
  const totalCount = useSelector((state: AppState) => state.NO03.totalCount);
  const loading = useSelector((state: AppState) => state.NO03.loading);
  const loadingMore = useSelector((state: AppState) => state.NO03.loadingMore);
  const hasMore = useSelector((state: AppState) => state.NO03.hasMore);
  const currentPage = useSelector((state: AppState) => state.NO03.currentPage);
  const lastSearchParams = useSelector((state: AppState) => state.NO03.lastSearchParams);
  const error = useSelector((state: AppState) => state.NO03.error);

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortDir('asc');
    }
  };

  const sortedInvoices = useMemo(() => {
    if (!sortBy) return invoices;
    return [...invoices].sort((a, b) => {
      const aVal = (a as any)[sortBy];
      const bVal = (b as any)[sortBy];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal), 'nb');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [invoices, sortBy, sortDir]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (amount: number): string => {
    return amount.toLocaleString('nb-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleViewInvoice = async (fakturanr: string) => {
    try {
      const companyId = lastSearchParams?.companyId ?? '';
      const response = await APICalls.getInvoicePDF(companyId, fakturanr);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Failed to load invoice PDF:', error);
      alert('Kunne ikke laste faktura PDF. Vennligst prøv igjen.');
    }
  };

  // Infinite scroll via IntersectionObserver on sentinel element
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore && lastSearchParams) {
          const nextPage = currentPage + 1;
          dispatch(actionsNO03.NO03LoadMoreRequest({
            ...lastSearchParams,
            page: nextPage,
            pageSize: 100,
          }));
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadingMore, hasMore, currentPage, lastSearchParams, dispatch]);

  if (loading) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.emptyState}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  if (invoices.length === 0) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.emptyState}>
          <Typography variant="h6">Ingen fakturaer funnet</Typography>
          <Typography variant="body2">
            Søk etter fakturaer ved å fylle ut søkeskjemaet ovenfor
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.container}>
      {/* Results Summary */}
      <Box className={classes.headerSection}>
        <Typography variant="h6" className={classes.resultSummary}>
          Fakturaer
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {totalCount} fakturaer — totalt {formatNumber(totalAmount)} kr
        </Typography>
      </Box>

      {/* Results Table */}
      <TableContainer sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <Table stickyHeader>
          <NO03TableHead sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
          <TableBody>
            {sortedInvoices.map((invoice: NO03, index: number) => (
              <TableRow
                key={`${invoice.fakturanr}-${index}`}
                className={`${classes.tableRow} ${invoice.fakturanr?.startsWith('9') ? classes.creditNote : ''}`}
              >
                <TableCell className={classes.cell}>{invoice.kundenr}</TableCell>
                <TableCell className={classes.cell}>{invoice.kundenavn}</TableCell>
                <TableCell className={classes.cell}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {invoice.fakturanr}
                    {invoice.fakturanr?.startsWith('9') && (
                      <Chip label="Kreditnota" size="small" sx={{ backgroundColor: 'rgba(239,68,68,0.15)', color: 'rgb(185,28,28)', fontWeight: 500, fontSize: '0.7rem', height: 20 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell className={classes.cell}>{invoice.fakturaKid}</TableCell>
                <TableCell className={classes.cell}>
                  {invoice.ordre ? (
                    <Tooltip title={invoice.ordre} placement="top">
                      <span style={{ cursor: 'default' }}>
                        {invoice.ordre.split(',')[0].trim()}
                        {invoice.ordre.includes(',') ? '…' : ''}
                      </span>
                    </Tooltip>
                  ) : ''}
                </TableCell>
                <TableCell className={classes.cell}>{formatDate(invoice.fakturadato)}</TableCell>
                <TableCell
                  align="right"
                  className={`${classes.cell} ${invoice.fakturatotal < 0 ? classes.amountNegative : classes.amountPositive}`}
                >
                  {formatNumber(invoice.fakturatotal)}
                </TableCell>
                <TableCell align="center" className={classes.cell}>
                  <Tooltip title="Vis faktura">
                    <IconButton
                      size="small"
                      onClick={() => handleViewInvoice(invoice.fakturanr)}
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Sentinel for infinite scroll — must stay inside the scrollable TableContainer, not
            after it, otherwise it never moves as the table itself scrolls and the
            IntersectionObserver never fires again after the initial mount. */}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </TableContainer>

      {/* Loading More Indicator */}
      {loadingMore && (
        <Box className={classes.loadMoreContainer}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Laster flere fakturaer...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
